/* eslint-disable @typescript-eslint/member-ordering */
import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import * as moment from "moment";
import { of } from "rxjs";
import { LocalStorageKeys } from "src/app/core/storage/local-storage";
import { AnyObject, UserTokens } from "src/app/shared/types/app.type";
import { environment } from "src/environments/environment";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { NotificationService } from "../../../shared/components/notification/notification.service";
import { LoginLayoutData } from "../index-layout/index-layout.type";

@Component({
  selector: "app-login",
  templateUrl: "./login.component.html",
  styleUrls: ["./login.component.scss"],
})
export class LoginComponent implements OnInit {
  @Output() adminHeader: EventEmitter<boolean> = new EventEmitter();
  readonly environment = environment;
  readonly defaultPath = "/dashboard";
  loginForm: FormGroup;
  loading = false;
  submitted = false;
  returnUrl: string;
  hostname: string;
  layoutData: LoginLayoutData = {
    image: "assets/GlobeLogo.svg",
    title: "Asset Manager",
  };

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private notificationService: NotificationService,
  ) {
    this.hostname = window.location.hostname;
    if (this.authenticationService.currentUserInfo) {
      this.router.navigate([this.defaultPath]);
    }
  }

  ngOnInit(): void {
    this.loginForm = this.formBuilder.group({
      email: ["", [Validators.required, Validators.maxLength(60), Validators.email]],
      password: ["", [Validators.required, Validators.minLength(5)]],
    });
  }

  get f() {
    return this.loginForm.controls;
  }

  onSubmit(): void {
    this.submitted = true;

    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;

    const email = this.f.email.value.trim().toLowerCase();
    const password = this.f.password.value;

    this.authenticationService.login(email, password).subscribe(
      (tokens: UserTokens) => {
        if (tokens.mfaToken) {
          const mfaData = this.getMFAData(tokens.mfaToken);
          localStorage.setItem(LocalStorageKeys.mfa, JSON.stringify(mfaData));
          this.router.navigate(["/multifactor-auth"]);
        } else
          this.authenticationService.setSession(of(tokens), false).subscribe(() => {
            const redirectUrl = this.route.snapshot.queryParams["redirectUrl"] || this.defaultPath;
            location.href = redirectUrl;
          });
      },
      (error) => {
        console.error(error);
        const err = error || "Error logging in user";
        this.notificationService.error(err);
        this.loading = false;
      },
    );
  }

  /**
   * Get the MFA data to persist
   * @param mfaToken MFA token
   * @returns
   */
  private getMFAData(mfaToken: string): AnyObject {
    let mfaData = { mfaToken };

    const codeDate = JSON.parse(localStorage.getItem(LocalStorageKeys.mfa))?.createdAt;

    // if the age of the old code is less than 1 minute, it will be deleted
    // otherwise we need to keep it to display the current timer
    if (codeDate) {
      const timeDifference = moment(moment().diff(codeDate));
      const showTimer = timeDifference.minutes() < 1;

      // if showTimer is true, we have to persist the previous data which includes createdAt value
      if (showTimer) {
        mfaData = { ...JSON.parse(localStorage.getItem(LocalStorageKeys.mfa)), mfaToken };
      }
    }

    return mfaData;
  }
}
